import * as Sentry from '@sentry/react';
import {ReactNode, useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'tolgee';

import {useModelsQuery} from '~api/models.queries';
import {
  inspectionTypeQueryParam,
  modelIdQueryParam,
  projectIdQueryParam,
  replaceRoute,
  Route,
  useQueryParam,
} from '~utils/routeUtil';
import {useAreFeatureFlagsReady} from '~hooks/launchDarkly.hooks';
import {useIsModelVisible} from '~hooks/useIsModelVisible';
import {InspectionType, Model} from '~redux/types/models';
import {annotatorActions} from '~redux/reducers/annotatorReducer';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {showNotification} from '~components/common/Notification';
import {ModelContext} from 'src/contexts/ModelContext';

/**
 *   - 'model-pages' all pages within the /model route have a model set
 *   - 'teach-session' regular teach sessions (a model can be selected) and smart teach sessions (a model is preselected according to the session label)
 *   - 'archive-annotator'  within the archive's image annotator, a model can be chosen
 *   - 'model-route-param' pages that identify their model through a path parameter instead of the `modelID` query param
 */
type ModelContextMode = 'model-pages' | 'teach-session' | 'archive-annotator' | 'model-route-param';

type ModelProviderProps = {children?: ReactNode} & (
  {mode: Exclude<ModelContextMode, 'model-route-param'>; modelId?: never} | {mode: 'model-route-param'; modelId: string}
);

/**
 * This provides the currently selected model within different unrelated contexts as defined by {@link ModelContextMode}.
 */
export function ModelProvider({children, ...props}: ModelProviderProps) {
  const modelsQuery = useModelsQuery();

  if (modelsQuery.isError || modelsQuery.isLoading) {
    return null;
  }

  // Create a new context for each mode, so that the current model is not shared between the different modes.
  switch (props.mode) {
    case 'model-pages':
      return <ModelProviderForModelPages>{children}</ModelProviderForModelPages>;
    case 'teach-session':
      return <ModelProviderForTeachSession>{children}</ModelProviderForTeachSession>;
    case 'archive-annotator':
      return <ModelProviderForArchiveAnnotator>{children}</ModelProviderForArchiveAnnotator>;
    case 'model-route-param':
      return <ModelProviderForRouteParam modelId={props.modelId}>{children}</ModelProviderForRouteParam>;
  }
}

function ModelProviderForModelPages({children}: {children?: React.ReactNode}) {
  const [currentModel, setCurrentModel] = useModelFromQueryParams({replacementRouteWhenModelNotFound: Route.models});
  const contextValue = useMemo(() => ({currentModel, setCurrentModel}), [currentModel, setCurrentModel]);

  return <ModelContext.Provider value={contextValue}>{children}</ModelContext.Provider>;
}

function ModelProviderForTeachSession({children}: {children?: React.ReactNode}) {
  const [currentModel, setCurrentModel] = useModelFromQueryParams({replacementRouteWhenModelNotFound: Route.tasks});
  const contextValue = useMemo(() => ({currentModel, setCurrentModel}), [currentModel, setCurrentModel]);

  return <ModelContext.Provider value={contextValue}>{children}</ModelContext.Provider>;
}

function ModelProviderForArchiveAnnotator({children}: {children?: React.ReactNode}) {
  const [currentModel, setCurrentModel] = useModelFromQueryParams({replacementRouteWhenModelNotFound: Route.archive});
  const contextValue = useMemo(() => ({currentModel, setCurrentModel}), [currentModel, setCurrentModel]);

  return <ModelContext.Provider value={contextValue}>{children}</ModelContext.Provider>;
}

function ModelProviderForRouteParam({modelId, children}: {modelId: string; children?: React.ReactNode}) {
  const modelsQuery = useModelsQuery();
  const currentModel = useMemo(
    () => modelsQuery.data?.find((model) => model.id === modelId),
    [modelsQuery.data, modelId],
  );
  const contextValue = useMemo(() => ({currentModel, setCurrentModel: () => undefined}), [currentModel]);

  return <ModelContext.Provider value={contextValue}>{children}</ModelContext.Provider>;
}

/**
 * Provides a getter and setter for the current model. The model ID is "stored" as a URL query parameter.
 *
 * **IMPORTANT:**
 * This is function is intended for holding the state of different model contexts and must only be used here (ModelProvider).
 * It is only exported to allow for testing.
 *
 * @param replacementRouteWhenModelNotFound If given, this determines the route we go to via replace when a model is not found.
 * @return A tuple consisting of
 *          - the current Model or undefined
 *          - a setter function to set the current model (or resetting it by passing undefined)
 */
export function useModelFromQueryParams({
  replacementRouteWhenModelNotFound,
}: {
  replacementRouteWhenModelNotFound?: Route;
} = {}): [model: Model | undefined, setModel: (model: Model | undefined) => void] {
  const {t} = useTranslation('models');
  const dispatch = useAppDispatch();
  const [modelIdParam, setModelIdParam] = useQueryParam(modelIdQueryParam);
  const [_, setInspectionTypeParam] = useQueryParam(inspectionTypeQueryParam);
  const modelsQuery = useModelsQuery();
  const isModelVisible = useIsModelVisible();
  const areFeatureFlagsReady = useAreFeatureFlagsReady();
  const [projectIdParam] = useQueryParam(projectIdQueryParam);
  const modelsProjectId = useAppSelector(selectCurrentProjectId);
  const modelsBelongToRoute =
    Boolean(modelsProjectId) && (typeof projectIdParam !== 'string' || projectIdParam === modelsProjectId);

  /**
   *  Returns either:
   * - `undefined` if the ID was not found in query params
   * - `null` if model does not exist
   * - `Model`
   */
  const currentModel = useMemo((): Model | undefined | null => {
    if (modelIdParam === undefined) {
      return undefined;
    } else {
      return modelsQuery.data?.find((model) => model.id === modelIdParam && isModelVisible(model)) ?? null;
    }
  }, [modelsQuery.data, modelIdParam, isModelVisible]);

  const setCurrentModel = useCallback(
    (model: Model | undefined) => {
      if (modelIdParam !== model?.id) {
        setModelIdParam(model?.id);
      }
      dispatch(annotatorActions.setCurrentModel(model));
      Sentry.setTag('modelId', model?.id);
    },
    [modelIdParam, dispatch, setModelIdParam],
  );

  const handleModelNotFound = useCallback(
    (modelId: string) => {
      setModelIdParam(undefined);

      setCurrentModel(undefined);
      showNotification({message: t('errorModelNotFound', {name: modelId}), severity: 'warning', id: 'model-not-found'});

      if (replacementRouteWhenModelNotFound) {
        replaceRoute(replacementRouteWhenModelNotFound, {
          keepModelIdQueryParam: false,
        });
      }
    },
    [replacementRouteWhenModelNotFound, setCurrentModel, setModelIdParam, t],
  );

  // If the modelId query param exists on load, update the current model based on the query param after the models query has finished.
  // When it does not exist, the model must be reset: the annotator keeps its own copy of the current model in redux
  // (annotatorReducer.currentModel) which nothing else clears, so a model selected on an earlier page would otherwise
  // leak into the archive annotator and filter its chips down to that model's (default) prediction.
  useEffect(() => {
    if (!modelsQuery.data || !modelsBelongToRoute || !areFeatureFlagsReady) {
      return;
    }

    if (modelIdParam === undefined || typeof modelIdParam !== 'string') {
      setCurrentModel(undefined);
      return;
    }

    if (currentModel === null) {
      handleModelNotFound(modelIdParam);
    } else {
      // When setting the current model, this will also update the inspection_type query param
      setCurrentModel(currentModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelsQuery.data, modelsBelongToRoute, areFeatureFlagsReady, isModelVisible]);

  useEffect(() => {
    if (currentModel) {
      if (currentModel.inspectionType === InspectionType.classification && currentModel.inspectionSubtype) {
        setInspectionTypeParam(`${currentModel.inspectionType}-${currentModel.inspectionSubtype}`);
      } else {
        setInspectionTypeParam(currentModel.inspectionType);
      }
    }
  }, [currentModel, setInspectionTypeParam]);

  return [currentModel ?? undefined, setCurrentModel];
}
